import PlaceIcon from '@mui/icons-material/Place';
import { Box, Dialog, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import cardIcon from '@/assets/images/icons/payment/card.svg';
import icCardIcon from '@/assets/images/icons/payment/ic_card.svg';
import moneyIcon from '@/assets/images/icons/payment/money.svg';
import qrCodeIcon from '@/assets/images/icons/payment/qr_code.svg';
import sumahoIcon from '@/assets/images/icons/payment/sumaho.svg';
import alertIcon from '@/assets/svg/alert-icon.svg';
import { CurrentPassengerInfoShow } from '@/components/pages/drive/details/currentPassengerInfoShow';
import type { ReservationInfo } from '@/components/pages/drive/details/driveDetails';
import { ReservationInfoComponent } from '@/components/pages/drive/details/reservationInfoComponent';
import { MbDialog } from '@/components/shared/modules';
import { CODE_MASTER_CODE, CODE_MASTER_TYPE } from '@/constants/code-master-type';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import type {
  Reservation,
  ReservationState,
  ReservationStatusCodeUpdateRequest,
  StationState,
  TimeInformation,
} from '@/features/driver/types/driveDetail';
import { generateStationKey, ReservationStatusCode, RouteStatus } from '@/features/driver/types/driveDetail';
import { formatMoney, getTimelineDataForReservationInfo } from '@/features/driver/utils/processData';
import * as subscriptions from '@/graphql/subscriptions';
import { apiPut } from '@/modules/hooks/use-put';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';
import * as paths from '@/routes/paths';
import { operationFinishConfirm } from '@/routes/paths';
import type { CodeMasterData } from '@/types/code-master';

/** 決済方法の表示名に対応するアイコンURL（該当なしは undefined） */
const PAYMENT_METHOD_ICON: Record<string, string> = {
  現金: moneyIcon,
  クレジットカード: cardIcon,
  電子決済: sumahoIcon,
  QRコード決済: qrCodeIcon,
  交通系IC: icCardIcon,
};

const MOCK_PAYMENT_METHODS: CodeMasterData[] = [
  { key: '1', value: '現金' },
  { key: '3', value: '回数券' },
];

const MAX_GROUP_SIZE = 5;

function getPaymentIconSrc(value: string): string | undefined {
  return PAYMENT_METHOD_ICON[value];
}

export const buildCurrentLocation = (
  stationRoutes: TimeInformation[],
  currentReservation?: ReservationInfo,
): TimeInformation[] | null => {
  if (!currentReservation) {
    return null;
  }

  const stationInfo = stationRoutes.filter((item) => String(item.waypointId) === currentReservation.waypointId);

  if (currentReservation.type === 1) {
    const matchingStations = stationInfo.filter((info) =>
      info?.pickup?.some((item) => item.reservationId === currentReservation.reservationId),
    );

    const reservationItem = matchingStations.map((info) => ({
      ...info,
      pickup: info.pickup.filter((item) => item.reservationId === currentReservation.reservationId),
      dropoff: [],
    }))?.[0];

    return reservationItem ? [reservationItem] : [];
  }

  const matchingStations = stationInfo.filter((info) =>
    info?.dropoff?.some((item) => item.reservationId === currentReservation.reservationId),
  );

  const reservationItem = matchingStations.map((info) => ({
    ...info,
    pickup: [],
    dropoff: info.dropoff.filter((item) => item.reservationId === currentReservation.reservationId),
  }))?.[0];

  return reservationItem ? [reservationItem] : [];
};

export const groupTimeInformation = (data: TimeInformation[]): number[][] => {
  const groups: number[][] = [];
  if (data.length === 0) return groups;

  let currentGroup: number[] = [0];

  for (let i = 1; i < data.length; i += 1) {
    if (data[i].waypointId === data[i - 1].waypointId) {
      currentGroup.push(i);
    } else {
      groups.push(currentGroup);
      currentGroup = [i];
    }
  }

  groups.push(currentGroup);

  return groups;
};

export const createGroupedConditions = (idList: string[], groupSize: number) => {
  const groups = [];
  for (let i = 0; i < idList.length; i += groupSize) {
    groups.push({ reservationId: { in: idList.slice(i, i + groupSize) } });
  }

  return groups;
};

type ReservationShowProps = {
  reservationList: Reservation[];
  isShow: boolean;
  vehicleOperationScheduleId: string;
  operationDate: string;
  operationZoneName: string;
  direction: string;
  driveReservationCount: number;
  drivePassengerCount: number;
  drivePrice: number;
  onMarkerClick: (markerId: string) => void;
  currentPassengerInfoShow: boolean;
  currentReservation?: ReservationInfo;
  toggle: boolean;
  updateRoutesType: (newRouteType: RouteStatus[]) => void;
  isDropoffMode?: boolean;
  mockShowReminderModal?: boolean;
  mockShowPaymentMethodSelectModal?: boolean;
  mockShowPaymentMethodOnlineModal?: boolean;
  mockShowDropoffConfirmModal?: boolean;
  mockShowUserCancelModal?: boolean;
  mockShowAdminUpdateModal?: boolean;
  mockShowCancelErrorModal?: boolean;
  /** Storybook用: 決済方法選択モーダルで表示するリスト（6個以上で6個目は「その他」） */
  mockPaymentMethodList?: CodeMasterData[];
  /** Storybook用: 乗車済み・降車済みなど完了状態を表示するための初期値 */
  mockInitialReservationCompletionState?: ReservationState;
  /** Storybook用: 到着済みとして表示する停留所の初期値 */
  mockInitialStationArrivalState?: StationState;
  /** Storybook用: 初期表示する停留所インデックス（0=1番目） */
  mockInitialWaypointIndex?: number;
};
type ErrorMsg = {
  message: string;
};

/**
 * 予約情報表示画面
 */
export const ReservationShow: React.FC<ReservationShowProps> = ({
  reservationList,
  isShow,
  vehicleOperationScheduleId,
  operationDate,
  operationZoneName,
  direction,
  driveReservationCount,
  drivePassengerCount,
  drivePrice,
  onMarkerClick,
  currentPassengerInfoShow,
  currentReservation,
  toggle: _toggle,
  updateRoutesType,
  isDropoffMode = false,
  mockShowReminderModal = false,
  mockShowPaymentMethodSelectModal = false,
  mockShowPaymentMethodOnlineModal = false,
  mockShowDropoffConfirmModal = false,
  mockShowUserCancelModal = false,
  mockShowAdminUpdateModal = false,
  mockShowCancelErrorModal = false,
  mockPaymentMethodList,
  mockInitialReservationCompletionState,
  mockInitialStationArrivalState,
  mockInitialWaypointIndex = 0,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stationRoutes, setStationRoutes] = useState<TimeInformation[]>([]);

  const [pickUpArrivalModalShow, setPickUpArrivalModalShow] = useState<boolean>(false);
  const [pickUpModalShow, setPickUpModalShow] = useState<boolean>(false);
  const [cancelModalShow, setCancelModalShow] = useState<boolean>(false);
  const [showReminderModal, setShowReminderModal] = useState(mockShowReminderModal);
  const [cancelMayModalShow, setCancelMayModalShow] = useState(false);
  const [isCurrentPassengerInfoVisible, setIsCurrentPassengerInfoVisible] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<TimeInformation[] | null>(null);
  const [paymentMethodSelectModalShow, setPaymentMethodSelectModalShow] = useState<boolean>(
    mockShowPaymentMethodSelectModal,
  );
  const [showOtherPaymentMethodsView, setShowOtherPaymentMethodsView] = useState<boolean>(false);
  const [paymentMethodOnlinePaymentSelectModalShow, setPaymentMethodOnlinePaymentSelectModalShow] = useState<boolean>(
    mockShowPaymentMethodOnlineModal,
  );
  const [dropoffConfirmModalShow, setDropoffConfirmModalShow] = useState(mockShowDropoffConfirmModal);

  const [pickUpArrivalInfo, setPickUpArrivalInfo] = useState<TimeInformation>();
  const [indexGroups, setIndexGroups] = useState<number[][]>([]);
  const [waypointIndex, setWaypointIndex] = useState<number>(mockInitialWaypointIndex);

  const [currentRecord, setCurrentRecord] = useState<Reservation>();
  const [reservationTime, setReservationTime] = useState<string | null>(null);
  const [paymentMethodSelectedValue, setPaymentMethodSelectedValue] = useState<number | null>(null);

  useEffect(() => {
    if (mockShowDropoffConfirmModal && reservationList.length > 0 && !currentRecord) {
      setCurrentRecord(reservationList[0]);
    }
  }, [mockShowDropoffConfirmModal, reservationList, currentRecord]);

  const [currentCancellation, setCurrentCancellation] = useState<Reservation>();
  const [currentUpdated, setCurrentUpdated] = useState<Reservation>();
  const [userCancelModalShow, setUserCancelModalShow] = useState<boolean>(false);
  const [adminUpdateModalShow, setAdminUpdateModalShow] = useState<boolean>(false);
  // エラー表示
  const [errorShow, setErrorShow] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (mockShowUserCancelModal && reservationList.length > 0) {
      setCurrentCancellation(reservationList[0]);
      setUserCancelModalShow(true);
    }
  }, [mockShowUserCancelModal, reservationList]);

  useEffect(() => {
    if (mockShowAdminUpdateModal && reservationList.length > 0) {
      setCurrentUpdated(reservationList[0]);
      setAdminUpdateModalShow(true);
    }
  }, [mockShowAdminUpdateModal, reservationList]);

  useEffect(() => {
    if (mockShowCancelErrorModal) {
      setErrorMessage('予約がキャンセルされました。「配車予約」に戻ります。（RSH80039）');
      setErrorShow(true);
    }
  }, [mockShowCancelErrorModal]);

  const [stationArrivalState, setStationArrivalState] = useState<StationState>(mockInitialStationArrivalState ?? {});
  const [reservationCompletionState, setReservationCompletionState] = useState<ReservationState>(
    mockInitialReservationCompletionState ?? {},
  );

  const [errorHandler, setErrorHandler] = useState<boolean>(false);
  const [errorShowFlg, setErrorShowFlg] = useState<boolean>(false);
  const [userCancelModalShowFlg, setUserCancelModalShowFlg] = useState<boolean>(false);
  const [adminUpdateModalShowFlg, setAdminUpdateModalShowFlg] = useState<boolean>(false);
  const paymentMethodInVehicle = useCodeMaster(CODE_MASTER_TYPE.PAYMENT_METHOD_IN_VEHICLE);

  const paymentMethodInVehicleShow: CodeMasterData[] = React.useMemo(() => {
    if (mockPaymentMethodList && mockPaymentMethodList.length > 0) {
      return mockPaymentMethodList;
    }
    const allowedMethods = currentRecord?.allowedPaymentMethods || [];
    const filtered = paymentMethodInVehicle.filter((item) => allowedMethods.includes(Number(item.key))) || [];
    if (filtered.length === 0 && (mockShowPaymentMethodSelectModal || mockShowPaymentMethodOnlineModal)) {
      return MOCK_PAYMENT_METHODS;
    }

    return filtered;
  }, [
    currentRecord,
    paymentMethodInVehicle,
    mockShowPaymentMethodSelectModal,
    mockShowPaymentMethodOnlineModal,
    mockPaymentMethodList,
  ]);

  useEffect(() => {
    setIsCurrentPassengerInfoVisible(currentPassengerInfoShow);

    const nextCurrentLocation = buildCurrentLocation(stationRoutes, currentReservation);
    if (nextCurrentLocation) {
      setCurrentLocation(nextCurrentLocation);
    }
  }, [currentPassengerInfoShow, currentReservation, stationRoutes, _toggle]);

  const updateReservationStatus = (
    reservationStatusCode: ReservationStatusCode,
    vehicleArrivedReservationIds?: { reservationId: number }[],
  ) => {
    let body: ReservationStatusCodeUpdateRequest = {
      reservationIdList: [{ reservationId: currentRecord?.reservationId || 0 }],
      reservationStatusCode,
    };

    // 車両到着済の場合（同一乗車地の全予約を渡す。未指定時は従来どおり押下したカードの pickup のみ）
    if (reservationStatusCode === ReservationStatusCode.VEHICLE_ARRIVED) {
      const ids =
        vehicleArrivedReservationIds ??
        pickUpArrivalInfo?.pickup?.map((item) => ({ reservationId: item.reservationId })) ??
        [];
      body = { ...body, reservationIdList: ids, isNotification: true };
    } else if (reservationStatusCode === ReservationStatusCode.RIDING) {
      // 乗車の場合
      body = { ...body };
    } else if (reservationStatusCode === ReservationStatusCode.DRIVE_CANCEL) {
      // ドライバキャンセルの場合
      body = { ...body, isNotification: true };
    } else if (reservationStatusCode === ReservationStatusCode.RIDE_COMPLETED) {
      // 降車完了の場合
      // 車内で支払う
      if (
        String(currentRecord?.paymentMethod) === CODE_MASTER_CODE.paymentMethodAtReservation_1 &&
        paymentMethodSelectedValue !== null
      ) {
        // TODO: payMethodCodeの型不整合を修正（フロント:数値 vs バック:文字列期待）
        // 現在はisNumberString()内でtoString()されるため動作するが、型安全性のため文字列統一が必要
        // 他への影響を考慮し別チケットで対応予定
        body = { ...body, payMethodCode: paymentMethodSelectedValue, isNotification: true };
      }
      // オンライン決済
      else if (String(currentRecord?.paymentMethod) === CODE_MASTER_CODE.paymentMethodAtReservation_2) {
        // TODO: 上記と同様の型不整合あり
        body = { ...body, payMethodCode: 2, isNotification: true };
      }
    }

    const response = apiPut(ApiNamePaths.reservationsStatus.apiName, ApiNamePaths.reservationsStatus.apiPath, { body });

    return response;
  };

  // fix: console "Blocked aria-hidden on a <xxx>"
  const clearAllFocusBeforeOpenDialog = (callBack: () => void) => {
    const elements = document.querySelectorAll('*');
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.blur();
      }
    });

    setTimeout(callBack);
  };

  // 乗車地点到着ボタン押下
  const handlePickUpArrival = (record: TimeInformation) => {
    clearAllFocusBeforeOpenDialog(() => {
      setPickUpArrivalInfo(record);
      setPickUpArrivalModalShow(true);
    });
  };

  // 乗車地点到着ボタン ⇒ 「はい」
  const handlePickUpArrivalYes = () => {
    const waypointSequence = pickUpArrivalInfo?.waypointSequence ?? '';
    const uniqueKey = generateStationKey(
      pickUpArrivalInfo?.waypointId ?? 0,
      pickUpArrivalInfo?.time || '',
      waypointSequence,
    );
    // 経路上の「今の停留所」のみ更新する（A→B→A→C で 1回目のA押下時に 2回目のAの客を更新しない）
    const currentGroupIndices = indexGroups[waypointIndex] ?? [];
    const currentStopItems = currentGroupIndices
      .map((index) => stationRoutes?.[index])
      .filter((item): item is TimeInformation => item != null);
    const vehicleArrivedReservationIds = currentStopItems.flatMap((item) =>
      (item.pickup ?? []).map((p) => ({ reservationId: p.reservationId })),
    );
    const keysForCurrentStop = currentStopItems.map((item) =>
      generateStationKey(item.waypointId ?? 0, item.time ?? '', item.waypointSequence ?? ''),
    );
    setStationArrivalState((prev) => ({
      ...prev,
      [uniqueKey]: { hasArrived: false, pickUpArrivalClicked: true },
    }));
    setPickUpArrivalModalShow(false);
    updateReservationStatus(ReservationStatusCode.VEHICLE_ARRIVED, vehicleArrivedReservationIds) // 車両到着済
      .then(() => {
        // 今の停留所の全時刻スロットのみ到着済みにする
        setStationArrivalState((prev) => {
          const next = { ...prev };
          keysForCurrentStop.forEach((key) => {
            next[key] = { hasArrived: true, pickUpArrivalClicked: true };
          });
          return next;
        });
        // 「乗車地点到着」ボタンを押下した時間 >=「乗車予定時刻＋60秒」の場合、すぐに乗車時刻警告確認ポップアップを表示する。
        const pickupDatetime = pickUpArrivalInfo?.time || '';
        if (
          dayjs().isAfter(dayjs(pickupDatetime).add(1, 'minute')) ||
          dayjs().isSame(dayjs(pickupDatetime).add(1, 'minute'))
        ) {
          setShowReminderModal(true);
        } else {
          // 「乗車地点到着」ボタンを押下した時間<「乗車予定時刻＋60秒」の場合、「乗車予定時刻＋60秒後」に乗車時刻警告確認ポップアップを表示する。
          setReservationTime(pickupDatetime);
        }
      })
      .catch((error: ErrorMsg) => {
        setStationArrivalState((prev) => ({
          ...prev,
          [uniqueKey]: { hasArrived: false, pickUpArrivalClicked: false },
        }));
        setErrorHandler(true);
        setErrorShowFlg(true);
        setErrorMessage(error.message);
      });
  };

  // 乗車時刻警告確認ポップアップ
  useEffect(() => {
    if (!reservationTime) return;

    const oneMinuteAfterReservation = dayjs(reservationTime).add(1, 'minute');

    const timer = setTimeout(() => {
      setShowReminderModal(true);
    }, oneMinuteAfterReservation.diff(dayjs()));

    return () => clearTimeout(timer);
  }, [reservationTime]);

  // ナビボタン
  const handleIconClick = (record: TimeInformation) => {
    const dest = `${record.latitude},${record.longitude}`;
    if (!record.latitude || !record.longitude) {
      return;
    }

    const daddr = encodeURIComponent(dest);
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${daddr}&directionsmode=driving&avoid=highways,tolls`;

    const ua = navigator.userAgent;
    const isMobilePhone = /iPhone|Android/.test(ua);
    if (isMobilePhone) {
      window.location.assign(googleMapsUrl);
      return;
    }

    window.open(googleMapsUrl, '_blank');
  };

  // 乗車ボタン
  const handlePickUp = (record: Reservation) => {
    clearAllFocusBeforeOpenDialog(() => {
      setCurrentRecord(record);
      setPickUpModalShow(true);
    });
  };

  // 乗車ボタン ⇒ 「はい」
  const handlePickUpYes = () => {
    if (currentRecord) {
      setReservationCompletionState((prev) => ({
        ...prev,
        [currentRecord.reservationId]: {
          isPickUpCompleted: false,
          isDropOffCompleted: false,
          pickUpClicked: true,
          cancelClicked: false,
          dropOffClicked: false,
        },
      }));
    }
    setPickUpModalShow(false);
    updateReservationStatus(ReservationStatusCode.RIDING) // 乗車中
      .then(() => {
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              isPickUpCompleted: true,
              isDropOffCompleted: false,
              pickUpClicked: true,
              cancelClicked: false,
              dropOffClicked: false,
            },
          }));
        }
      })
      .catch((error: ErrorMsg) => {
        setErrorHandler(true);
        setErrorShowFlg(true);
        setErrorMessage(error.message);
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              isPickUpCompleted: false,
              isDropOffCompleted: false,
              pickUpClicked: false,
              cancelClicked: false,
              dropOffClicked: false,
            },
          }));
        }
      });
  };

  // キャンセルボタン
  const handleCancel = (record: Reservation) => {
    clearAllFocusBeforeOpenDialog(() => {
      setCurrentRecord(record);
      if (dayjs().isBefore(dayjs(reservationTime).add(1, 'minute'))) {
        setCancelMayModalShow(true);
      } else {
        setCancelModalShow(true);
      }
    });
  };

  // キャンセルボタン ⇒ 「はい」
  const handleCancelYes = () => {
    if (currentRecord) {
      setReservationCompletionState((prev) => ({
        ...prev,
        [currentRecord.reservationId]: {
          isPickUpCompleted: false,
          isDropOffCompleted: false,
          pickUpClicked: false,
          cancelClicked: true,
          dropOffClicked: false,
        },
      }));
    }
    setCancelModalShow(false);
    void updateReservationStatus(ReservationStatusCode.DRIVE_CANCEL) // ドライバキャンセル
      .then(() => {
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              isPickUpCompleted: true,
              isDropOffCompleted: true,
              pickUpClicked: false,
              cancelClicked: true,
              dropOffClicked: false,
            },
          }));
        }
      })
      .catch((error: ErrorMsg) => {
        setErrorHandler(true);
        setErrorShowFlg(true);
        setErrorMessage(error.message);
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              isPickUpCompleted: false,
              isDropOffCompleted: false,
              pickUpClicked: false,
              cancelClicked: false,
              dropOffClicked: false,
            },
          }));
        }
      });
  };

  // 降車ボタン
  const handleDropoff = (record: Reservation) => {
    clearAllFocusBeforeOpenDialog(() => {
      setCurrentRecord(record);

      // recordから直接許可された決済方法を取得し、最初の値を初期値として設定
      if (record.allowedPaymentMethods && record.allowedPaymentMethods.length > 0) {
        setPaymentMethodSelectedValue(record.allowedPaymentMethods[0]);
      }

      // 車内で支払う
      if (String(record.paymentMethod) === CODE_MASTER_CODE.paymentMethodAtReservation_1) {
        setShowOtherPaymentMethodsView(false);
        setPaymentMethodSelectModalShow(true);
      }
      // オンライン決済
      else if (String(record.paymentMethod) === CODE_MASTER_CODE.paymentMethodAtReservation_2) {
        setPaymentMethodOnlinePaymentSelectModalShow(true);
      }
    });
  };

  // 降車ボタン ⇒ 「はい」
  const handleDropoffYes = () => {
    if (currentRecord) {
      setReservationCompletionState((prev) => ({
        ...prev,
        [currentRecord.reservationId]: {
          ...prev[currentRecord.reservationId],
          isDropOffCompleted: false,
          dropOffClicked: true,
        },
      }));
    }
    setDropoffConfirmModalShow(false);
    updateReservationStatus(ReservationStatusCode.RIDE_COMPLETED) // 降車完了
      .then(() => {
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              ...prev[currentRecord.reservationId],
              isDropOffCompleted: true,
              dropOffClicked: true,
            },
          }));
        }
      })
      .catch((error: ErrorMsg) => {
        setErrorHandler(true);
        setErrorShowFlg(true);
        setErrorMessage(error.message);
        if (currentRecord) {
          setReservationCompletionState((prev) => ({
            ...prev,
            [currentRecord.reservationId]: {
              ...prev[currentRecord.reservationId],
              isDropOffCompleted: false,
              dropOffClicked: false,
            },
          }));
        }
      });
  };

  useEffect(() => {
    if (indexGroups && indexGroups.length > 0) {
      const areAllReservationsCompleted = indexGroups[waypointIndex].every(
        (index) =>
          stationRoutes?.[index]?.pickup?.every(
            (pickupInfo) => reservationCompletionState[pickupInfo.reservationId]?.isPickUpCompleted,
          ) &&
          stationRoutes?.[index]?.dropoff?.every(
            (dropoffInfo) => reservationCompletionState[dropoffInfo.reservationId]?.isDropOffCompleted,
          ),
      );
      if (areAllReservationsCompleted) {
        if (waypointIndex !== null && waypointIndex < indexGroups.length - 1) {
          setWaypointIndex(waypointIndex + 1);
          setReservationTime(null);
          setCurrentLocation(null);

          const waypointId = stationRoutes?.[indexGroups[waypointIndex + 1][0]].waypointId;
          const hasPickup = stationRoutes
            .filter((item) => item.waypointId === waypointId)
            .some((info) => info.pickup.length > 0);
          const hasDropoff = stationRoutes
            .filter((item) => item.waypointId === waypointId)
            .some((info) => info.dropoff.length > 0);

          if (hasPickup && hasDropoff) {
            onMarkerClick(`${waypointId},${waypointId}`);
          } else {
            onMarkerClick(`${waypointId}`);
          }

          const newRouteType = Array<RouteStatus>(stationRoutes.length - 1).fill(RouteStatus.SCHEDULED);

          for (let i = 0; i < newRouteType.length; i += 1) {
            if (i < indexGroups[waypointIndex][indexGroups[waypointIndex].length - 1]) {
              newRouteType[i] = RouteStatus.FINISHED;
            } else if (i === indexGroups[waypointIndex][indexGroups[waypointIndex].length - 1]) {
              newRouteType[i] = RouteStatus.RUNNING;
            }
          }
          updateRoutesType(newRouteType);
        } else {
          // 運行終了確認画面を遷移する
          setTimeout(() => {
            navigate(operationFinishConfirm, {
              state: { vehicleOperationScheduleId },
            });
          }, 1000);
        }
      }
    }
  }, [
    navigate,
    stationRoutes,
    vehicleOperationScheduleId,
    reservationCompletionState,
    indexGroups,
    waypointIndex,
    updateRoutesType,
    onMarkerClick,
  ]);

  useEffect(() => {
    const timelineData = getTimelineDataForReservationInfo(reservationList);
    const groups = groupTimeInformation(timelineData);
    setIndexGroups(groups);
    setStationRoutes(timelineData);
  }, [reservationList]);

  const isDecideDisabled = paymentMethodSelectedValue === null;

  useEffect(() => {
    // カードの表示=>最新のステータスが反映
    const initialStationState: StationState = {};
    const initialReservationState: ReservationState = {};

    reservationList.forEach((reservation) => {
      const id = reservation.reservationId;
      const key = generateStationKey(
        reservation.pickupWaypointId || 0,
        reservation.latestEstimatedPickupDatetime || '',
        reservation.pickupWaypointSequence || '',
      );
      const reservationStatus = Number(reservation.status) as unknown as ReservationStatusCode;

      if (!initialStationState[key]) {
        initialStationState[key] = { hasArrived: false, pickUpArrivalClicked: false };
      }

      if (reservationStatus >= ReservationStatusCode.VEHICLE_ARRIVED) {
        initialStationState[key].hasArrived = true;
        initialStationState[key].pickUpArrivalClicked = true;
      }

      initialReservationState[id] = {
        isPickUpCompleted:
          reservationStatus === ReservationStatusCode.RIDING ||
          reservationStatus === ReservationStatusCode.RIDE_COMPLETED ||
          reservationStatus === ReservationStatusCode.DRIVE_CANCEL,
        isDropOffCompleted:
          reservationStatus === ReservationStatusCode.RIDE_COMPLETED ||
          reservationStatus === ReservationStatusCode.DRIVE_CANCEL,
        pickUpClicked:
          reservationStatus === ReservationStatusCode.RIDING ||
          reservationStatus === ReservationStatusCode.RIDE_COMPLETED,
        cancelClicked: reservationStatus === ReservationStatusCode.DRIVE_CANCEL,
        dropOffClicked: reservationStatus === ReservationStatusCode.RIDE_COMPLETED,
      };
    });

    if (!mockInitialStationArrivalState) {
      setStationArrivalState(initialStationState);
    }
    if (!mockInitialReservationCompletionState) {
      setReservationCompletionState(initialReservationState);
    }
  }, [reservationList, mockInitialStationArrivalState, mockInitialReservationCompletionState]);

  // 通知監視
  // 配車予約ステータスリアルタイム取得
  const getReservationStatus = useCallback(async () => {
    const client = generateClient();
    const idList = reservationList.map((reservation) => String(reservation.reservationId));

    const variables = {
      filter: {
        or: createGroupedConditions(idList, MAX_GROUP_SIZE),
      },
    };

    const createSub = client.graphql({ query: subscriptions.onUpdateReservationStatus, variables }).subscribe({
      next: ({ data }) => {
        if (idList.includes(data.onUpdateReservationStatus.reservationId)) {
          // ユーザキャンセル
          // 通知監視にてキャンセル通知があった場合
          const status = Number(data.onUpdateReservationStatus.status) as unknown as ReservationStatusCode;
          if (
            status === ReservationStatusCode.RESERVATION_CANCELED_BEFORE_DEADLINE ||
            status === ReservationStatusCode.RESERVATION_CANCELED_AFTER_DEADLINE
          ) {
            const reservationInfo = reservationList.find(
              (reservation) => String(reservation.reservationId) === data.onUpdateReservationStatus.reservationId,
            );

            if (reservationInfo) {
              setCurrentCancellation(reservationInfo);
              setErrorHandler(true);
              setUserCancelModalShowFlg(true);
            }
          }

          // 管理者側でステータス更新
          // 予約完了、オペレータキャンセル（ドライバ管理者）、オペレータキャンセル（サービス事業者）にステータス更新があった場合

          if (
            status === ReservationStatusCode.RESERVATION_COMPLETED ||
            status === ReservationStatusCode.OPERATOR_CANCEL_BEFORE_DEADLINE_DRIVER_ADMIN ||
            status === ReservationStatusCode.OPERATOR_CANCEL_AFTER_DEADLINE_DRIVER_ADMIN ||
            status === ReservationStatusCode.OPERATOR_CANCEL_BEFORE_DEADLINE_SERVICE_PROVIDER ||
            status === ReservationStatusCode.OPERATOR_CANCEL_AFTER_DEADLINE_SERVICE_PROVIDER
          ) {
            setCurrentUpdated(
              reservationList.find(
                (reservation) => String(reservation.reservationId) === data.onUpdateReservationStatus.reservationId,
              ),
            );
            setErrorHandler(true);
            setAdminUpdateModalShowFlg(true);
          }
        }
      },
      error: (err) => console.warn(err), // eslint-disable-line no-console
    });

    return () => {
      createSub.unsubscribe();
    };
  }, [reservationList]);

  useEffect(() => {
    void getReservationStatus();
  }, [getReservationStatus]);

  useEffect(() => {
    if (errorHandler) {
      if (!userCancelModalShow && !adminUpdateModalShow && !errorShow) {
        if (userCancelModalShowFlg) {
          setUserCancelModalShow(true);
        } else if (adminUpdateModalShowFlg) {
          setAdminUpdateModalShow(true);
        } else if (errorShowFlg) {
          setErrorShow(true);
        }
        setErrorHandler(false);
      }
    }
  }, [
    errorHandler,
    userCancelModalShowFlg,
    adminUpdateModalShowFlg,
    errorShowFlg,
    userCancelModalShow,
    adminUpdateModalShow,
    errorShow,
  ]);

  const handleCloseDialog = () => {
    setUserCancelModalShow(false);
    setUserCancelModalShowFlg(false);
    setErrorHandler(false);
    window.location.reload();
  };

  const handleCloseUpdatedDialog = () => {
    setAdminUpdateModalShow(false);
    setAdminUpdateModalShowFlg(false);
    setErrorHandler(false);
    window.location.reload();
  };

  const handleCloseErrorDialog = () => {
    setErrorShow(false);
    setErrorShowFlg(false);
    setErrorHandler(false);
    if (errorMessage?.includes('RSH80039')) {
      navigate(paths.driveList);
    }

    setErrorMessage('');
  };

  const activeArrivalTarget = React.useMemo(() => {
    if (!indexGroups.length || !stationRoutes.length) {
      return undefined;
    }
    const activeIndexes = indexGroups[waypointIndex] || [];
    const candidate = activeIndexes
      .map((index) => stationRoutes[index])
      .find((route) => route && route.pickup.length > 0 && !route.dropoff.length);
    if (!candidate) {
      return undefined;
    }
    const key = generateStationKey(candidate.waypointId || 0, candidate.time || '', candidate.waypointSequence || '');
    if (stationArrivalState[key]?.pickUpArrivalClicked) {
      return undefined;
    }

    return candidate;
  }, [indexGroups, waypointIndex, stationRoutes, stationArrivalState]);

  return (
    isShow && (
      <>
        <div className="reservation-info-panel">
          <div className="reservation-info-panel-top">
            <div className="reservation-summary-row">
              <div className="reservation-summary-card">
                <div className="reservation-summary-card-inner">
                  <p className="reservation-summary-date">
                    {dayjs(operationDate).locale('ja').format('M月D日（ddd）')}
                  </p>
                  <div className="reservation-summary-meta">
                    <p className="reservation-summary-meta-text">
                      <span className="meta-number">{driveReservationCount}</span>
                      <span className="meta-unit">{t('driveDetail.passengerCount')}</span>
                      <span className="meta-number">{drivePassengerCount}</span>
                      <span className="meta-unit">{t('driveDetail.people')}</span>
                    </p>
                    <p className="reservation-summary-meta-text">
                      <span className="meta-number">{formatMoney(drivePrice)}</span>
                      <span className="meta-unit">{t('driveDetail.yen')}</span>
                    </p>
                    <p className="reservation-summary-meta-text">
                      {operationZoneName}
                      {direction && direction !== '-' ? ` ${direction}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="reservation-info-content">
            <div className="reservation-main-row">
              <div className="reservation-list-wrap">
                {stationRoutes?.map((item, index) => {
                  const activeGroup = indexGroups[waypointIndex] ?? [];
                  const firstActiveIndex = activeGroup[0] ?? Number.POSITIVE_INFINITY;
                  const isActive = activeGroup.includes(index);

                  return (
                    <ReservationInfoComponent
                      key={item.key}
                      data={item}
                      isLastest={index === stationRoutes.length - 1}
                      isBefore={index < firstActiveIndex}
                      active={isActive}
                      onPickUp={handlePickUp}
                      onCancel={handleCancel}
                      onDropoff={handleDropoff}
                      stationArrivalState={stationArrivalState}
                      reservationCompletionState={reservationCompletionState}
                      hideActionCards={isDropoffMode}
                      headerRight={
                        isActive ? (
                          <div className="station-card-header-right-buttons">
                            <button
                              className="reservation-map-button reservation-map-button--in-station"
                              type="button"
                              onClick={() => {
                                handleIconClick(item);
                              }}
                            >
                              <PlaceIcon className="reservation-map-button__icon" aria-hidden />
                              <span className="reservation-map-button__text">
                                <span className="reservation-map-button__text-line">Map</span>
                                <span className="reservation-map-button__text-line reservation-map-button__text-line--sub">
                                  で見る
                                </span>
                              </span>
                            </button>
                            {activeArrivalTarget && item.key === activeArrivalTarget.key ? (
                              <button
                                type="button"
                                className="reservation-arrival-button reservation-arrival-button--in-header"
                                onClick={() => handlePickUpArrival(activeArrivalTarget)}
                              >
                                {t('driveDetail.pickUpArrival')}
                              </button>
                            ) : null}
                          </div>
                        ) : null
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <CurrentPassengerInfoShow
          data={currentLocation || indexGroups?.[waypointIndex]?.map((index) => stationRoutes?.[index])}
          isShow={isCurrentPassengerInfoVisible}
        />

        {/* 乗車地点到着確認ポップアップ */}
        <MbDialog
          open={pickUpArrivalModalShow}
          content={t('driveDetail.pickUpArrivalMessage')}
          okLabel={t('driveDetail.yes')}
          cancelLabel={t('driveDetail.no')}
          onOk={() => {
            handlePickUpArrivalYes();
          }}
          onCancel={() => {
            setPickUpArrivalModalShow(false);
          }}
        />

        {/* 乗車確認ポップアップ */}
        <MbDialog
          open={pickUpModalShow}
          content={`${currentRecord?.userName.split(' ')[0]}${t('driveDetail.pickUpMessage')}`}
          okLabel={t('driveDetail.yes')}
          cancelLabel={t('driveDetail.no')}
          onOk={() => {
            handlePickUpYes();
          }}
          onCancel={() => {
            setPickUpModalShow(false);
          }}
        />

        {/* 予約キャンセル確認ポップアップ */}
        <MbDialog
          open={cancelModalShow}
          content={[
            `${currentRecord?.userName.split(' ')[0]}${t('driveDetail.cancelMessage1')}`,
            t('driveDetail.cancelMessage2'),
          ]}
          okLabel={t('driveDetail.yes')}
          cancelLabel={t('driveDetail.no')}
          onOk={() => {
            handleCancelYes();
          }}
          onCancel={() => {
            setCancelModalShow(false);
          }}
        />

        {/* 乗車時刻警告確認ポップアップ */}
        <Dialog
          open={showReminderModal}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.13)',
              width: '90%',
              margin: '16px',
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '32px',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '40px',
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={alertIcon} alt="" width={32} height={32} />
            </Box>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}
            >
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1e293b', lineHeight: '31.2px' }}>
                {t('driveDetail.rideTimeWarningMessage1')}
              </Typography>
              <Box sx={{ color: '#64748b', fontSize: 20, lineHeight: 1.7 }}>
                <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit' }}>
                  {t('driveDetail.rideTimeWarningMessage2')}
                </Typography>
                <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit' }}>
                  {t('driveDetail.rideTimeWarningMessage3')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => {
              setShowReminderModal(false);
            }}
            sx={{
              width: '100%',
              height: 100,
              borderRadius: '12px',
              backgroundColor: '#003ecf',
              boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
              border: 'none',
              color: '#ffffff',
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.4,
              letterSpacing: '1.6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#0032a8' },
            }}
          >
            {t('driveDetail.yes')}
          </Box>
        </Dialog>

        {/* 予約キャンセル経過確認ポップアップ */}
        <MbDialog
          open={cancelMayModalShow}
          content={[t('driveDetail.cancelMayMessage1'), t('driveDetail.cancelMayMessage2')]}
          okLabel={t('driveDetail.yes')}
          onOk={() => {
            setCancelMayModalShow(false);
          }}
        />

        {/* 決済方法選択画面_車内で支払う */}
        <Dialog
          open={paymentMethodSelectModalShow}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.13)',
              width: '90%',
              maxWidth: '600px',
              margin: '16px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '32px',
            },
          }}
        >
          {showOtherPaymentMethodsView ? (
            <>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#475569', textAlign: 'center' }}>
                {t('driveDetail.otherPaymentMethods')}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '16px',
                }}
              >
                {paymentMethodInVehicleShow && paymentMethodInVehicleShow.length >= 6
                  ? paymentMethodInVehicleShow.slice(5).map((option) => {
                      const numericKey = Number(option.key);
                      const isSelected = paymentMethodSelectedValue === numericKey;
                      const iconSrc = getPaymentIconSrc(option.value);

                      return (
                        <Box
                          key={option.key}
                          component="button"
                          type="button"
                          onClick={() => setPaymentMethodSelectedValue(numericKey)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '16px',
                            border: isSelected ? '2px solid #003ecf' : '1px solid #e5e7eb',
                            backgroundColor: isSelected ? '#e0ecff' : '#ffffff',
                            boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.12)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            '&:hover': { backgroundColor: isSelected ? '#d4e4ff' : '#f8fafc' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              minWidth: 40,
                              minHeight: 40,
                              borderRadius: '12px',
                              backgroundColor: 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {iconSrc ? (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt=""
                                sx={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
                                aria-hidden
                              />
                            ) : null}
                          </Box>
                          <Typography
                            component="span"
                            sx={{ fontSize: 15, fontWeight: 700, color: isSelected ? '#003ecf' : '#111827' }}
                          >
                            {option.value}
                          </Typography>
                        </Box>
                      );
                    })
                  : null}
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', width: '100%', height: 100 }}>
                <Box
                  component="button"
                  onClick={() => setShowOtherPaymentMethodsView(false)}
                  sx={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                    color: '#64748b',
                    fontSize: 32,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: '1.6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  {t('driveDetail.back')}
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={isDecideDisabled}
                  onClick={() => {
                    if (isDecideDisabled) return;
                    setPaymentMethodSelectModalShow(false);
                    setShowOtherPaymentMethodsView(false);
                    setDropoffConfirmModalShow(true);
                  }}
                  sx={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '12px',
                    backgroundColor: isDecideDisabled ? '#93c5fd' : '#003ecf',
                    border: 'none',
                    boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                    color: '#ffffff',
                    fontSize: 32,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: '1.6px',
                    cursor: isDecideDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: isDecideDisabled ? '#93c5fd' : '#0032a8' },
                    opacity: isDecideDisabled ? 0.7 : 1,
                  }}
                >
                  {t('driveDetail.decide')}
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#475569', textAlign: 'center' }}>
                {t('driveDetail.paymentMethodSelect')}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '16px',
                }}
              >
                {(paymentMethodInVehicleShow && paymentMethodInVehicleShow.length >= 6
                  ? paymentMethodInVehicleShow.slice(0, 6)
                  : paymentMethodInVehicleShow
                )?.map((option, index) => {
                  const numericKey = Number(option.key);
                  const isSelected = paymentMethodSelectedValue === numericKey;
                  const isSixthAsOther =
                    paymentMethodInVehicleShow != null && paymentMethodInVehicleShow.length >= 6 && index === 5;
                  const displayLabel = isSixthAsOther ? 'その他' : option.value;
                  const iconSrc = isSixthAsOther ? undefined : getPaymentIconSrc(option.value);
                  const handleClick = () => {
                    if (isSixthAsOther) {
                      setPaymentMethodSelectedValue(null);
                      setShowOtherPaymentMethodsView(true);
                    } else {
                      setPaymentMethodSelectedValue(numericKey);
                    }
                  };

                  return (
                    <Box
                      key={option.key}
                      component="button"
                      type="button"
                      onClick={handleClick}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '16px',
                        border: isSelected ? '2px solid #003ecf' : '2px solid #e5e7eb',
                        backgroundColor: isSelected ? '#e0ecff' : '#ffffff',
                        boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.12)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        '&:hover': {
                          backgroundColor: isSelected ? '#d4e4ff' : '#f8fafc',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          minHeight: 40,
                          borderRadius: '12px',
                          backgroundColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {iconSrc ? (
                          <Box
                            component="img"
                            src={iconSrc}
                            alt=""
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'contain',
                              display: 'block',
                            }}
                            aria-hidden
                          />
                        ) : null}
                      </Box>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: isSelected ? '#003ecf' : '#111827',
                        }}
                      >
                        {displayLabel}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', width: '100%', height: 100 }}>
                <Box
                  component="button"
                  onClick={() => {
                    setPaymentMethodSelectModalShow(false);
                    setShowOtherPaymentMethodsView(false);
                  }}
                  sx={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                    color: '#64748b',
                    fontSize: 32,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: '1.6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  {t('driveDetail.back')}
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={isDecideDisabled}
                  onClick={() => {
                    if (isDecideDisabled) return;
                    setPaymentMethodSelectModalShow(false);
                    setShowOtherPaymentMethodsView(false);
                    setDropoffConfirmModalShow(true);
                  }}
                  sx={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '12px',
                    backgroundColor: isDecideDisabled ? '#93c5fd' : '#003ecf',
                    border: 'none',
                    boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                    color: '#ffffff',
                    fontSize: 32,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: '1.6px',
                    cursor: isDecideDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: isDecideDisabled ? '#93c5fd' : '#0032a8' },
                    opacity: isDecideDisabled ? 0.7 : 1,
                  }}
                >
                  {t('driveDetail.decide')}
                </Box>
              </Box>
            </>
          )}
        </Dialog>

        {/* 決済方法選択画面_オンライン決済 */}
        <MbDialog
          open={paymentMethodOnlinePaymentSelectModalShow}
          okLabel={t('driveDetail.onlinePaymentSelect')}
          onOk={() => {
            setPaymentMethodOnlinePaymentSelectModalShow(false);
            setDropoffConfirmModalShow(true);
          }}
        >
          <Box sx={{ backgroundColor: '#ffffff', px: 1, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
              {' '}
              {t('driveDetail.paymentMethodOnlinePayment1')}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
              {' '}
              {t('driveDetail.paymentMethodOnlinePayment2')}
            </Typography>
          </Box>
        </MbDialog>

        {/* 降車地点到着確認ポップアップ */}
        <Dialog
          open={dropoffConfirmModalShow}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.13)',
              width: '90%',
              maxWidth: '480px',
              margin: '16px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '32px',
            },
          }}
        >
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#475569', textAlign: 'center' }}>
            {t('driveDetail.dropoffConfirmMessage')}
          </Typography>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.reservationId')}</Typography>
              <Typography className="reservation-detail-info-value reservation-detail-info-value-column">
                {currentRecord?.reservationId}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.name')}</Typography>
              <Typography className="reservation-detail-info-value reservation-detail-info-value-column">
                {`${currentRecord?.userName.split(' ')[0]}${t('driveDetail.mr')}`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.passenger')}</Typography>
              <Typography className="reservation-detail-info-value reservation-detail-info-value-column">
                {currentRecord?.passengerTypeList
                  .map(
                    (passenger) =>
                      `${passenger.passengerTypeName} ${passenger.passengerCount}${t('driveDetail.people')}`,
                  )
                  .join(' ')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.pickupWaypoint')}</Typography>
              <Box className="reservation-detail-info-right">
                <Box
                  sx={{
                    backgroundColor: '#f0f9ff',
                    borderRadius: '4px',
                    px: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography className="reservation-detail-waypoint-number">
                    {currentRecord?.pickupWaypointNumber}
                  </Typography>
                </Box>
                <Typography className="reservation-detail-info-value">{currentRecord?.pickupWaypointName}</Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.dropoffWaypoint')}</Typography>
              <Box className="reservation-detail-info-right">
                <Box
                  sx={{
                    backgroundColor: '#f0f9ff',
                    borderRadius: '4px',
                    px: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography className="reservation-detail-waypoint-number">
                    {currentRecord?.dropoffWaypointNumber}
                  </Typography>
                </Box>
                <Typography className="reservation-detail-info-value">{currentRecord?.dropoffWaypointName}</Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                py: '16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography className="reservation-detail-info-label">{t('driveDetail.total')}</Typography>
              <Typography className="reservation-detail-info-value reservation-detail-info-value-column">
                {`${formatMoney(currentRecord?.price)}${t('driveDetail.yen')}`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '16px', width: '100%', height: 100 }}>
            <Box
              component="button"
              onClick={() => {
                setDropoffConfirmModalShow(false);
              }}
              sx={{
                flex: 1,
                height: '100%',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                color: '#64748b',
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.4,
                letterSpacing: '1.6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              {t('driveDetail.no')}
            </Box>
            <Box
              component="button"
              onClick={() => {
                handleDropoffYes();
              }}
              sx={{
                flex: 1,
                height: '100%',
                borderRadius: '12px',
                backgroundColor: '#003ecf',
                border: 'none',
                boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
                color: '#ffffff',
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.4,
                letterSpacing: '1.6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#0032a8' },
              }}
            >
              {t('driveDetail.yes')}
            </Box>
          </Box>
        </Dialog>

        {/* ユーザキャンセルポップアップ */}
        <MbDialog
          open={userCancelModalShow}
          okLabel={t('driveDetail.confirm')}
          onOk={() => {
            handleCloseDialog();
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              p: '32px',
            }}
          >
            {/* ヘッダー */}
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#64748b',
                color: '#fff',
                textAlign: 'center',
                py: 2,
                px: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 24, lineHeight: 1.375 }}>
                {t('driveDetail.cancelText')}
              </Typography>
            </Box>
            {/* 注意メッセージ */}
            <Typography
              sx={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                lineHeight: 1.4,
                letterSpacing: '0.9px',
              }}
            >
              {t('driveDetail.notificationMessage1')}
              <br />
              {t('driveDetail.notificationMessage2')}
            </Typography>
            {/* 詳細行（Figma順: お名前 → 乗車時刻 → 乗車地 → 降車時刻 → 降車地） */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.name')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {currentCancellation?.userName.split(' ')[0]}
                  {t('driveDetail.mr')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.pickupTime')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {dayjs(currentCancellation?.latestEstimatedPickupDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.pickupWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {currentCancellation?.pickupWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {currentCancellation?.pickupWaypointName}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.dropoffTime')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {dayjs(currentCancellation?.latestEstimatedDropoffDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.dropoffWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {currentCancellation?.dropoffWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {currentCancellation?.dropoffWaypointName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </MbDialog>

        {/* ステータス更新ポップアップ表示（管理者更新・Figma 1615:17748） */}
        <MbDialog
          open={adminUpdateModalShow}
          okLabel={t('driveDetail.confirm')}
          onOk={() => {
            handleCloseUpdatedDialog();
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              p: '32px',
            }}
          >
            {/* ヘッダー（赤枠・赤文字） */}
            <Box
              sx={{
                width: '100%',
                border: '1px solid #ff4b00',
                color: '#ff4b00',
                textAlign: 'center',
                py: 2,
                px: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 24, lineHeight: 1.4 }}>
                {t('driveDetail.cancelText')}
              </Typography>
            </Box>
            {/* 注意メッセージ */}
            <Typography
              sx={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                lineHeight: 1.4,
                letterSpacing: '0.9px',
              }}
            >
              {t('driveDetail.notificationMessage1')}
              <br />
              {t('driveDetail.notificationMessage2')}
            </Typography>
            {/* 詳細行（Figma順: お名前 → 乗車時刻 → 乗車地 → 降車時刻 → 降車地） */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.name')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {currentUpdated?.userName.split(' ')[0]}
                  {t('driveDetail.mr')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.pickupTime')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {dayjs(currentUpdated?.latestEstimatedPickupDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.pickupWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {currentUpdated?.pickupWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {currentUpdated?.pickupWaypointName}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('driveDetail.dropoffTime')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {dayjs(currentUpdated?.latestEstimatedDropoffDatetime).format('HH:mm')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.dropoffWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {currentUpdated?.dropoffWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {currentUpdated?.dropoffWaypointName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </MbDialog>

        {/* errorポップアップ */}
        <Dialog
          open={errorShow}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.13)',
              width: '90%',
              // maxWidth: '400px',
              margin: '16px',
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '20px',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>
            <Box
              sx={{
                border: '1px solid #ff4b00',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
              }}
            >
              <Typography
                sx={{ fontSize: 24, fontWeight: 700, color: '#ff4b00', textAlign: 'center', lineHeight: '33px' }}
              >
                {errorMessage}
              </Typography>
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => {
              handleCloseErrorDialog();
            }}
            sx={{
              width: '100%',
              height: 100,
              borderRadius: '12px',
              backgroundColor: '#003ecf',
              boxShadow: '0px 6px 0px 0px rgba(0, 0, 0, 0.25)',
              border: 'none',
              color: '#ffffff',
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.4,
              letterSpacing: '1.6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#0032a8' },
            }}
          >
            {t('driveDetail.yes')}
          </Box>
        </Dialog>
      </>
    )
  );
};
